import "./appPresenceBadge.scss";

import {
    getChannel,
    getChannelState,
    getChannelFaults,
    getDriverAppState,
} from "utils/appPresence";
import { formatDateTime, ltrText } from "utils/dateFormat";

/**
 * Whether ONE surface was seen recently — the restaurant app, the driver app,
 * or the web dashboard.
 *
 * One badge per channel, never a merged "online" dot: a vendor with the
 * dashboard open and the kitchen tablet off is exactly the case an operator
 * needs to spot, and merging the two would report it as fine.
 *
 * @param {object} props
 * @param {object} props.presence  The row's `appPresence` object from the API.
 * @param {"vendorApp"|"driverApp"|"vendorWeb"|"adminWeb"} props.channel
 * @param {string} props.neverLabel  Wording when this surface has never been seen.
 * @param {boolean} [props.isOnline] Driver rows only — the self-declared toggle.
 */
const AppPresenceBadge = ({ presence, channel, neverLabel, isOnline }) => {
    const data = getChannel(presence, channel);

    const { tone, label } =
        channel === "driverApp"
            ? getDriverAppState(!!isOnline, presence)
            : getChannelState(data, neverLabel);

    const faults = getChannelFaults(data);

    const title = [
        data.lastSeenAt
            ? `آخر ظهور: ${ltrText(formatDateTime(data.lastSeenAt))}`
            : "لم نستلم أي إشارة من هذا التطبيق",
        data.appVersion ? `الإصدار: ${data.appVersion}` : null,
        data.platform ? `النظام: ${data.platform}` : null,
        // Only a heartbeat reports state; saying so keeps a reader from
        // over-trusting a stale poll-derived reading.
        data.source === "poll" ? "مستنتج من نشاط التطبيق، وليس نبضة مباشرة" : null,
        ...faults,
    ]
        .filter(Boolean)
        .join("\n");

    return (
        <span className={`app-presence app-presence--${tone}`} title={title}>
            <span className="app-presence__dot" aria-hidden="true" />
            {label}
            {faults.length > 0 ? (
                <span className="app-presence__fault" title={faults.join("\n")}>
                    ⚠
                </span>
            ) : null}
        </span>
    );
};

export default AppPresenceBadge;
